import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellProps,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {NamespaceAvatarContainer} from '../../avatars/namespaceAvatarContainer';

/*
 * Props.
 */

export interface NamespaceCellProps extends TableCellProps<string> {
  iconSize?: VisualSizesEnum;
}

/*
 * Styles.
 */

const StyledNamespaceCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledLabelDivProps {
  $isSelected: boolean;
}

const StyledLabelDiv = styled.div<StyledLabelDivProps>`
  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

/*
 * Component.
 */

export const NamespaceCell = (props: NamespaceCellProps): ReactElement => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    iconSize = VisualSizesEnum.SMALL,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  return (
    <TableCell<string>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledNamespaceCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledLabelDiv $isSelected={false}>
            <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledLabelDiv>
        </StyledNamespaceCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledNamespaceCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <NamespaceAvatarContainer
            namespace={value}
            iconSize={iconSize}
            isSelected={Boolean(isSelected)}
            shouldRenderLabel
          />
        </StyledNamespaceCellContainerDiv>
      )}
    />
  );
};
